import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Check, Copy, Download } from "lucide-react";
import { getProfessionnelPatients, type ProfessionnelPatientRow } from "../../../api/professional";
import { searchPatients } from "../../../api/professionalPatient";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import { formatFrenchDate } from "../../../lib/patientSummary";
import ConsultationsBadge from "../../../components/patients/ConsultationsBadge";

ModuleRegistry.registerModules([AllCommunityModule]);

// ─── Types ────────────────────────────────────────────────────────────────────

type PatientRow = {
  id: string;
  nip: string;
  nom: string;
  prenom: string;
  email: string;
  addedAt: string;
  derniereConsultation: string;
  nombreConsultations: number;
};

/** NIP patient attendu par l'API (aligné création / HELPEUR). */
const PATIENT_NIP_OK = /^\d{10}$/;

// ─── AG Grid styles (fond transparent pour s'intégrer au layout Pro) ──────────

const AG_PATIENTS_VARS: React.CSSProperties = {
  "--ag-background-color": "rgba(255,255,255,0.0)",
  "--ag-foreground-color": "#0B1F16",
  "--ag-secondary-foreground-color": "rgba(11,31,22,0.55)",
  "--ag-header-background-color": "rgba(255,255,255,0.0)",
  "--ag-header-foreground-color": "#0B1F16",
  "--ag-border-color": "rgba(0,0,0,0.08)",
  "--ag-row-border-color": "rgba(0,0,0,0.08)",
  "--ag-odd-row-background-color": "rgba(0,0,0,0.0)",
  "--ag-even-row-background-color": "rgba(0,0,0,0.0)",
  "--ag-row-hover-color": "rgba(255,255,255,0.55)",
  "--ag-selected-row-background-color": "rgba(0,85,62,0.08)",
  "--ag-range-selection-border-color": "#00553E",
  "--ag-checkbox-checked-color": "#00553E",
  "--ag-input-focus-border-color": "#00553E",
  "--ag-font-family": "Inter, sans-serif",
  "--ag-font-size": "0.875rem",
  "--ag-cell-horizontal-padding": "24px",
  "--ag-wrapper-border-radius": "6px",
  "--ag-header-height": "48px",
  "--ag-row-height": "56px",
  "--ag-header-column-separator-display": "none",
  "--ag-pagination-background-color": "transparent",
} as React.CSSProperties;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function flattenPatientApiRow(o: Record<string, unknown>): Record<string, unknown> {
  for (const key of ["patient", "dossier", "patientInfo", "patientDto", "fiche"] as const) {
    const n = o[key];
    if (n && typeof n === "object" && !Array.isArray(n)) {
      return { ...(n as Record<string, unknown>), ...o };
    }
  }
  return o;
}

function pickPatientNip(m: Record<string, unknown>): string {
  return String(
    m.nip ?? m.patientNip ?? m.anip ?? m.patient_nip ??
    m.patientNIP ?? m.numeroNational ?? m.numero_patient_nip ?? ""
  ).trim();
}

function parseAnyDateLabel(dateStr: string): Date | null {
  if (!dateStr || dateStr === "—") return null;
  const ts = Date.parse(dateStr);
  if (!Number.isNaN(ts)) return new Date(ts);
  const [dd, mm, yyyy] = dateStr.split("/").map(Number);
  if (!yyyy || !mm || !dd) return null;
  return new Date(yyyy, mm - 1, dd);
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapProfessionnelRow(r: ProfessionnelPatientRow, index: number): PatientRow {
  const nip = r.nip?.trim() ?? "";
  return {
    id: nip || `row-${index}`,
    nip,
    nom: r.nom?.toUpperCase() ?? "",
    prenom: r.prenom ?? "",
    email: r.email ?? "",
    addedAt: formatFrenchDate(r.derniereConsultation),
    derniereConsultation: formatFrenchDate(r.derniereConsultation),
    nombreConsultations: Number(r.nombreConsultations ?? 0),
  };
}

function mapSearchRow(r: unknown, index: number): PatientRow {
  if (!r || typeof r !== "object") {
    return { id: `u${index}`, nip: "", nom: "", prenom: "", email: "", addedAt: "—", derniereConsultation: "—", nombreConsultations: 0 };
  }
  const m = flattenPatientApiRow(r as Record<string, unknown>);
  const nip = pickPatientNip(m);
  const id = nip || (m.id != null && String(m.id) !== "" ? `id-${String(m.id)}` : `search-${index}`);
  return {
    id,
    nip,
    nom: String(m.nom ?? "").toUpperCase(),
    prenom: String(m.prenom ?? ""),
    email: String(m.email ?? ""),
    addedAt: formatFrenchDate(m.dateCreation ?? m.createdAt),
    derniereConsultation: "—",
    nombreConsultations: 0,
  };
}

// ─── Copy hook + bouton ───────────────────────────────────────────────────────

function useCopy(text: string) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return { copied, copy };
}

function CopyBtn({ text }: { text: string }) {
  const { copied, copy } = useCopy(text);
  return (
    <button
      onClick={copy}
      title="Copier"
      className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 rounded p-0.5 hover:bg-black/5 text-[#0B1F16]/50"
    >
      {copied
        ? <Check size={12} className="text-green-600" />
        : <Copy size={12} />}
    </button>
  );
}

// ─── Cell Renderers ───────────────────────────────────────────────────────────

function NipCellRenderer({ value }: ICellRendererParams<PatientRow>) {
  const nip = String(value ?? "");
  return (
    <div className="group flex items-center justify-between h-full gap-1">
      <span className="font-mono">{nip}</span>
      <CopyBtn text={nip} />
    </div>
  );
}

function MaskedCellRenderer() {
  return (
    <div className="flex items-center h-full">
      <span className="tracking-widest text-[#0B1F16]/25 select-none">••••••••</span>
    </div>
  );
}

function MaskedEmailCellRenderer({ value }: ICellRendererParams<PatientRow>) {
  const email = String(value ?? "");
  return (
    <div className="group flex items-center justify-between h-full gap-1">
      <span className="tracking-widest text-[#0B1F16]/25 select-none">••••••••••••</span>
      {email && <CopyBtn text={email} />}
    </div>
  );
}

function ConsultationsBadgeCellRenderer({ value }: ICellRendererParams<PatientRow>) {
  return (
    <div className="flex items-center h-full">
      <ConsultationsBadge count={Number(value ?? 0)} />
    </div>
  );
}

// ─── NIP digit inputs ────────────────────────────────────────────────────────

const glassStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.18) 100%)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(255,255,255,0.45)",
};

function NipDigitInputs({
  digits,
  onChange,
  nipRefs,
}: {
  digits: string[];
  onChange: (d: string[]) => void;
  nipRefs: React.MutableRefObject<Array<HTMLInputElement | null>>;
}) {
  const handleChange = (i: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = digit;
    onChange(next);
    if (digit && i < 9) nipRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!digits[i] && i > 0) {
        const next = [...digits];
        next[i - 1] = "";
        onChange(next);
        nipRefs.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft"  && i > 0) nipRefs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < 9) nipRefs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    const next = Array.from({ length: 10 }, (_, k) => pasted[k] ?? "");
    onChange(next);
    nipRefs.current[Math.min(pasted.length, 9)]?.focus();
  };

  return (
    <div className="flex items-center gap-1.5">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { nipRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className="w-7 h-8 text-center text-sm font-mono bg-white/20 rounded-[4px] outline-none text-white caret-white transition-all focus:bg-white/35 focus:ring-1 focus:ring-white/60"
          style={{ borderBottom: `2px solid ${d ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.4)"}` }}
          placeholder="·"
        />
      ))}
    </div>
  );
}

// ─── Options période ──────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: "tout",     label: "Tout" },
  { value: "30 jours", label: "30 jours" },
  { value: "60 jours", label: "60 jours" },
  { value: "90 jours", label: "90 jours" },
  { value: "06 mois",  label: "6 mois" },
  { value: "09 mois",  label: "9 mois" },
];

const PERIOD_DAYS: Record<string, number> = {
  "30 jours": 30,
  "60 jours": 60,
  "90 jours": 90,
  "06 mois":  182,
  "09 mois":  273,
};

// ─── Composant principal ──────────────────────────────────────────────────────

export default function ProPatients() {
  const navigate = useNavigate();
  const { professional } = useProfessionalAuth();
  const proNip = professional?.nip?.trim();
  const gridRef = useRef<AgGridReact<PatientRow>>(null);
  const nipRefs = useRef<Array<HTMLInputElement | null>>(Array(10).fill(null));

  const [searchMode, setSearchMode] = useState<"nip" | "nom">("nom");
  const [nipDigits, setNipDigits] = useState<string[]>(Array(10).fill(""));
  const [nameQuery, setNameQuery] = useState("");
  const [period, setPeriod] = useState("tout");
  const [rows, setRows] = useState<PatientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Query effectif : NIP complet (10 chiffres) ou texte nom
  const query = useMemo(() => {
    if (searchMode === "nip") {
      const full = nipDigits.join("");
      return full.length === 10 ? full : "";
    }
    return nameQuery;
  }, [searchMode, nipDigits, nameQuery]);

  const switchMode = (mode: "nip" | "nom") => {
    setSearchMode(mode);
    if (mode === "nip") setNameQuery("");
    else setNipDigits(Array(10).fill(""));
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    const q = query.trim();
    try {
      if (q) {
        const params: Record<string, string | number | undefined> = /^\d+$/.test(q) ? { nip: q } : { nom: q };
        const res = await searchPatients(params);
        if (!res.ok) { setListError(res.message); setRows([]); return; }
        setRows((res.data as unknown[]).map(mapSearchRow));
        return;
      }
      if (!proNip) {
        setListError("Profil professionnel sans NIP — reconnectez-vous ou complétez votre profil.");
        setRows([]);
        return;
      }
      const res = await getProfessionnelPatients(proNip, { limit: 500, offset: 0 });
      if (!res.ok) {
        const fallback = await searchPatients({ statut: "actif" });
        if (fallback.ok) {
          setRows((fallback.data as unknown[]).map(mapSearchRow));
          setListError("Liste principale indisponible — résultats élargis (recherche).");
        } else {
          setListError(res.message);
          setRows([]);
        }
        return;
      }
      setRows(res.data.map(mapProfessionnelRow));
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Erreur réseau");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [proNip, query]);

  useEffect(() => {
    const t = window.setTimeout(() => void load(), query.trim() ? 350 : 0);
    return () => window.clearTimeout(t);
  }, [load, query]);

  // ── Filtre période (seul filtre conservé côté client, AG Grid gère le reste) ─

  const filteredRows = useMemo(() => {
    const days = PERIOD_DAYS[period];
    if (days == null) return rows;
    return rows.filter((row) => {
      const d = parseAnyDateLabel(row.addedAt);
      if (!d || Number.isNaN(d.getTime())) return true;
      return (Date.now() - d.getTime()) / 86_400_000 <= days;
    });
  }, [rows, period]);

  // ── AG Grid config ─────────────────────────────────────────────────────────

  const colDefs = useMemo<ColDef<PatientRow>[]>(
    () => [
      {
        field: "nip",
        headerName: "NUM ANIP",
        flex: 1.4,
        minWidth: 140,
        cellRenderer: NipCellRenderer,
        sort: "asc",
      },
      {
        field: "nom",
        headerName: "NOM",
        flex: 1,
        minWidth: 100,
        cellRenderer: MaskedCellRenderer,
        sortable: false,
      },
      {
        field: "prenom",
        headerName: "PRÉNOM",
        flex: 1,
        minWidth: 100,
        cellRenderer: MaskedCellRenderer,
        sortable: false,
      },
      {
        field: "email",
        headerName: "E-MAIL",
        flex: 1.6,
        minWidth: 150,
        cellRenderer: MaskedEmailCellRenderer,
        sortable: false,
        filter: false,
      },
      {
        field: "derniereConsultation",
        headerName: "Dernière consultation",
        flex: 1.2,
        minWidth: 160,
      },
      {
        field: "nombreConsultations",
        headerName: "Consultations",
        flex: 0.9,
        minWidth: 110,
        cellRenderer: ConsultationsBadgeCellRenderer,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({ sortable: true, filter: true, resizable: true }),
    []
  );

  // ── Rendu ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Barre de contrôles */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-[300px]">
          {/* Toggle NIP / Nom */}
          <div className="flex rounded-[6px] overflow-hidden shrink-0" style={glassStyle}>
            {(["nom", "nip"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`h-12 px-4 text-sm font-semibold transition-colors ${
                  searchMode === m ? "bg-white/30 text-white" : "text-white/55 hover:text-white/80"
                }`}
              >
                {m === "nip" ? "NIP" : "Nom"}
              </button>
            ))}
          </div>

          {/* Zone saisie selon le mode */}
          {searchMode === "nom" ? (
            <div
              className="h-12 flex-1 max-w-sm rounded-[6px] px-4 flex items-center"
              style={glassStyle}
            >
              <input
                placeholder="Recherche par nom"
                className="flex-1 bg-transparent outline-none placeholder-white/70 text-sm text-white"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
              />
            </div>
          ) : (
            <div
              className="h-12 rounded-[6px] px-4 flex items-center"
              style={glassStyle}
            >
              <NipDigitInputs
                digits={nipDigits}
                onChange={setNipDigits}
                nipRefs={nipRefs}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-12 rounded-[6px] px-3 text-sm font-semibold text-white/90 outline-none cursor-pointer"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.18) 100%)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.45)",
            }}
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-gray-900 bg-white">
                {o.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            title="Bientôt disponible"
            disabled
            className="h-12 px-6 rounded-[6px] text-white/70 font-semibold inline-flex items-center gap-2 cursor-not-allowed"
            style={{ background: "#00553E", boxShadow: "0 2px 0 rgba(0,0,0,0.1)", opacity: 0.65 }}
          >
            Exporter <Download size={16} />
          </button>
        </div>
      </div>

      {listError && (
        <div className="rounded-[8px] border border-amber-200/80 bg-amber-50/90 px-4 py-2 text-sm text-amber-950">
          {listError}
        </div>
      )}

      {/* Tableau AG Grid */}
      <div
        className="ag-theme-quartz overflow-hidden rounded-[6px]"
        style={{
          ...AG_PATIENTS_VARS,
          background: "linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.72) 100%)",
          backdropFilter: "blur(6px)",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        {loading ? (
          <div className="flex h-40 items-center justify-center text-sm text-[#0B1F16]/60">Chargement…</div>
        ) : (
          <AgGridReact<PatientRow>
            key={query}
            ref={gridRef}
            rowData={filteredRows}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            quickFilterText={searchMode === "nom" ? nameQuery : ""}
            domLayout="autoHeight"
            animateRows
            pagination
            paginationPageSize={10}
            rowHeight={56}
            headerHeight={48}
            suppressMovableColumns
            getRowId={(p) => p.data.id}
            getRowClass={(p) =>
              PATIENT_NIP_OK.test(p.data?.nip ?? "")
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-60"
            }
            onRowClicked={(e) => {
              if (!e.data || !PATIENT_NIP_OK.test(e.data.nip)) return;
              navigate(`/pro/dashboard/patients/${encodeURIComponent(e.data.nip)}`);
            }}
            noRowsOverlayComponent={() => (
              <span className="text-sm text-[#0B1F16]/60">
                {query.trim() ? "Aucun patient trouvé." : "Vous n'avez encore consulté aucun patient."}
              </span>
            )}
          />
        )}
      </div>

      <p className="text-center text-sm text-[#0B1F16]/70">
        {loading ? "Chargement…" : `${filteredRows.length} patient(s)`}
      </p>
    </div>
  );
}
